import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logger } from './lib/logger';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
import { Input } from './components/ui/Input';
import { Select } from './components/ui/Select';
import { typography } from './theme';
import { Search, ArrowRight, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { USER_TYPE_INFO, USER_TYPES } from '@shared/user-types';
import { BulkUserTypeUpdateResponseSchema } from '@shared/schemas/admin-access';
import { ApiProblemError, useApiFetchJson } from './lib/apiFetchJson';
import type { AdminUsersListUser } from './api/admin-users.schema';
import { useAdminUsers } from './api/useAdminUsers';

type UserTypeValue = (typeof USER_TYPES)[number];

/** Colors per user type for the column header accent */
const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  MEMBER: { bg: 'bg-nightshade-50', text: 'text-nightshade-700', border: 'border-nightshade-200' },
  PRE_SALES: { bg: 'bg-blueberry-50', text: 'text-blueberry-700', border: 'border-blueberry-200' },
  POST_SALES: { bg: 'bg-matcha-50', text: 'text-matcha-700', border: 'border-matcha-200' },
  REVOPS: { bg: 'bg-ube-50', text: 'text-ube-700', border: 'border-ube-200' },
  SDR: { bg: 'bg-tangerine-50', text: 'text-tangerine-700', border: 'border-tangerine-200' },
};

// ─── Component ───────────────────────────────────────────────────────

export default function AdminUserTypes() {
  const apiFetchJson = useApiFetchJson();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveTarget, setMoveTarget] = useState<UserTypeValue | (string & {})>('');

  // Clear selection when search changes — "store prior value" pattern during
  // render so hidden users are de-selected in the same render as the filter.
  const [lastSearch, setLastSearch] = useState(search);
  if (lastSearch !== search) {
    setLastSearch(search);
    setSelectedIds(new Set());
  }

  const { data, isLoading, error } = useAdminUsers();

  // Bulk update mutation
  const bulkUpdate = useMutation({
    mutationFn: async ({ userIds, userType }: { userIds: number[]; userType: string }) => {
      try {
        return await apiFetchJson(BulkUserTypeUpdateResponseSchema, '/api/admin/users/bulk-type', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userIds, userType }),
        });
      } catch (err) {
        // The write committed server-side but the reply did not match its
        // schema: treat it as done without a count, never as a red failure.
        if (err instanceof ApiProblemError && err.kind === 'malformed') return null;
        throw err;
      }
    },
    onSuccess: (result) => {
      toast.success(result ? `Updated ${result.updated} user${result.updated === 1 ? '' : 's'}` : 'Users updated');
      setSelectedIds(new Set<number>());
      setMoveTarget('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  // Group users by type
  const grouped = useMemo(() => {
    const users = data?.users ?? [];
    const lowerSearch = search.toLowerCase();
    const filtered = lowerSearch
      ? users.filter(
          (u) =>
            u.ownerEmail?.toLowerCase().includes(lowerSearch) ||
            u.slackUserId.toLowerCase().includes(lowerSearch),
        )
      : users;

    const groups: Record<string, AdminUsersListUser[]> = {};
    for (const type of USER_TYPES) {
      groups[type] = [];
    }
    for (const user of filtered) {
      const bucket = groups[user.userType];
      if (bucket) {
        bucket.push(user);
      } else {
        logger.warn(`[AdminUserTypes] Unknown userType: ${user.userType} for user ${user.id}`);
        groups.MEMBER.push(user);
      }
    }
    return groups;
  }, [data, search]);

  const toggleUser = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = (type: string) => {
    const users = grouped[type] ?? [];
    const allSelected = users.every((u) => selectedIds.has(u.id));
    const next = new Set(selectedIds);
    for (const u of users) {
      if (allSelected) next.delete(u.id);
      else next.add(u.id);
    }
    setSelectedIds(next);
  };

  const handleMove = () => {
    if (!moveTarget || selectedIds.size === 0) return;
    bulkUpdate.mutate({ userIds: Array.from(selectedIds), userType: moveTarget });
  };

  if (error) {
    return (
      <div className="p-6">
        <p className="text-content-danger">Failed to load users: {(error).message}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className={typography.headingLg}>User Types</h1>
        <p className={`${typography.body} mt-1`}>
          Manage user type assignments. User types control which UI surfaces are visible.
        </p>
      </div>

      {/* Toolbar: search + action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary" />
          <Input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-blueberry-200 bg-blueberry-50 px-3 py-2">
            <span className="text-sm font-medium text-blueberry-700">
              {selectedIds.size} selected
            </span>
            <ArrowRight className="h-4 w-4 text-blueberry-400" />
            <Select
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              className="w-48"
            >
              <option value="">Move to...</option>
              {USER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {USER_TYPE_INFO[type]?.label ?? type}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              size="sm"
              disabled={!moveTarget || bulkUpdate.isPending}
              loading={bulkUpdate.isPending}
              onClick={handleMove}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Move
            </Button>
            <button
              onClick={() => { setSelectedIds(new Set<number>()); setMoveTarget(''); }}
              className="ml-1 text-nightshade-400 hover:text-nightshade-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
        </div>
      )}

      {/* Type columns grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
          {USER_TYPES.map((type) => {
            const users = grouped[type] ?? [];
            const info = USER_TYPE_INFO[type];
            const colors = TYPE_COLORS[type] ?? TYPE_COLORS.MEMBER;
            const allSelected = users.length > 0 && users.every((u) => selectedIds.has(u.id));
            const someSelected = users.some((u) => selectedIds.has(u.id));

            return (
              <div key={type}>
                <Card noPadding className="flex flex-col overflow-hidden">
                {/* Header */}
                <div className={`px-4 py-3 ${colors.bg} border-b ${colors.border}`}>
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-semibold ${colors.text}`}>
                      {info?.label ?? type}
                    </h3>
                    <span className={`text-xs font-medium ${colors.text} tabular-nums`}>
                      {users.length}
                    </span>
                  </div>
                  <p className="text-xs text-content-tertiary mt-0.5">
                    {info?.description ?? ''}
                  </p>
                </div>

                {/* User list */}
                <div className="flex-1 overflow-y-auto max-h-[420px]">
                  {users.length === 0 ? (
                    <div className="px-4 py-8 text-center text-content-tertiary text-xs">
                      {search ? 'No matches' : 'No users'}
                    </div>
                  ) : (
                    <div className="divide-y divide-nightshade-100">
                      {/* Select all row */}
                      <label className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-nightshade-50 text-xs text-content-tertiary">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el: HTMLInputElement | null) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => toggleAll(type)}
                          className="rounded border-nightshade-300 text-blueberry-500 focus:ring-blueberry-200"
                        />
                        Select all
                      </label>

                      {users.map((user) => (
                        <label
                          key={user.id}
                          className={`flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-nightshade-50 transition-colors ${
                            selectedIds.has(user.id) ? 'bg-blueberry-50/50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(user.id)}
                            onChange={() => toggleUser(user.id)}
                            className="rounded border-nightshade-300 text-blueberry-500 focus:ring-blueberry-200"
                          />
                          <span className="text-sm text-content-secondary truncate">
                            {user.ownerEmail ?? user.slackUserId}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
